<template>
  <!-- The page an admin's setup link opens, without logging in. The link's secret is after '#' (location.hash),
       so it is never sent in a url or kept in a server log; the API gets it in the request body. -->
  <div id="setup">
    <div class="box" v-loading="loading">
      <img v-if="info && info.logo_url" class="logo" :src="info.logo_url" :alt="info.admin_company_name"/>
      <div class="title">Set up autopay</div>

      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon></el-alert>

      <template v-if="done">
        <el-result icon="success" title="Autopay is on"
          :subTitle="'Invoices of ' + done.company_name + ' will be charged to ' + cardName(done.card) + ' on their due date.'">
        </el-result>
        <p class="hint center">You can close this page.</p>
      </template>

      <template v-else-if="info">
        <p class="lead">
          <b>{{ info.admin_company_name }}</b> invites <b>{{ info.company_name }}</b> to pay its invoices automatically
          with a saved card.
        </p>
        <p class="hint" v-if="info.autopay_enabled && info.current_card">
          Autopay is already on with {{ cardName(info.current_card) }}. Saving a card here switches autopay to it.
        </p>

        <form onsubmit="return false;">
          <div class="group">
            <label>
              <span>Card number</span>
              <div id="staxjs-number" class="input-box"></div>
            </label>
            <div class="groupDiv">
              <label>
                <span>CVV</span>
                <div id="staxjs-cvv" class="input-box"></div>
              </label>
              <label>
                <span>Expiration date</span>
                <el-date-picker v-model="form.expiration" style="width: 100%;" type="month"
                  placeholder="MM/YYYY" format="MM/yyyy" value-format="MM/yyyy" placement="bottom-start">
                </el-date-picker>
              </label>
            </div>
            <label>
              <span>Name on card</span>
              <div class="names">
                <input v-model="form.first_name" autocomplete="off" class="input-box" placeholder="First name"/>
                <input v-model="form.last_name" autocomplete="off" class="input-box" placeholder="Last name"/>
              </div>
            </label>
          </div>
          <p class="hint">Your card number goes directly to our payment processor (Stax) and is never stored by us.</p>

          <div v-if="info.past_due_invoices.length > 0" class="pastDue">
            <p>These invoices are already past due:</p>
            <ul>
              <li v-for="inv in info.past_due_invoices" :key="inv.client_invoice_uuid">
                {{ inv.invoice_label }} · due {{ date(inv.due_date) }} · ${{ money(inv.amount) }}
              </li>
            </ul>
            <el-checkbox v-model="form.include_past_due">Also pay these with autopay (total ${{ money(pastDueTotal) }})</el-checkbox>
          </div>

          <div class="consent">
            <el-checkbox v-model="form.consent"></el-checkbox>
            <span>{{ info.consent_text }}</span>
          </div>
          <label class="signer">
            <span>Your name (who agrees)</span>
            <input v-model="form.signer_name" autocomplete="name" class="input-box" placeholder="Full name"/>
          </label>

          <div class="buttons">
            <span class="btn" :class="{ disabled: !canSubmit }" @click="submit" v-loading="saving">
              Save card and turn on autopay
            </span>
          </div>
          <p class="hint center">This link works once and expires on {{ dateTime(info.expires_datetime) }} (UTC).</p>
        </form>
      </template>
    </div>
  </div>
</template>
<script>
  import { postActionApi } from '@/network/manageApi'
  import moment from 'moment/moment'

  export default {
    name: 'AutopaySetup',
    data() {
      return {
        token: '',
        loading: false,
        saving: false,
        error: '',
        info: null,
        done: null,
        staxjs: null,
        formComplete: false,
        formError: 'Please enter the card number and CVV',
        form: { expiration: '', first_name: '', last_name: '', include_past_due: false, consent: false, signer_name: '' },
      }
    },
    computed: {
      pastDueTotal() {
        return this.info ? this.info.past_due_invoices.reduce((sum, inv) => sum + inv.amount, 0) : 0
      },
      canSubmit() {
        return this.form.consent && !!this.form.signer_name.trim()
      },
    },
    mounted() {
      this.token = decodeURIComponent((window.location.hash || '').replace(/^#/, ''))
      if (!this.token) {
        this.error = 'This link is not complete. Please open the full link you were sent.'
        return
      }
      this.load()
    },
    methods: {
      load() {
        this.loading = true
        postActionApi('/payment/autopay-link', { token: this.token }).then(res => {
          this.info = res.data
          this.$nextTick(() => this.initCardForm())
        }).catch(err => {
          this.error = this.message(err, 'This link could not be opened. Please ask for a new one.')
        }).finally(() => {
          this.loading = false
        })
      },
      message(err, fallback) {
        try {
          return err.response.data.details.data.message || fallback
        } catch (e) {
          return fallback
        }
      },
      initCardForm() {
        this.staxjs = new StaxJs(process.env.VUE_APP_STAX_TOKEN, {
          number: { id: 'staxjs-number', placeholder: '0000 0000 0000 0000', style: 'height: 30px; width: 100%; font-size: 15px;',
                    type: 'text', format: 'prettyFormat' },
          cvv: { id: 'staxjs-cvv', placeholder: 'CVV', style: 'height: 30px; width: 100%; font-size: 15px;' },
        })
        this.staxjs.showCardForm().catch(err => this.$message.error(String(err)))
        this.staxjs.on('card_form_complete', () => {
          this.formComplete = true
        })
        this.staxjs.on('card_form_incomplete', (message) => {
          this.formComplete = false
          this.formError = !message.validNumber ? 'Please enter the correct card number' : 'Please enter the correct CVV'
        })
      },
      submit() {
        if (!this.formComplete) return this.$message.error(this.formError)
        if (!this.form.expiration) return this.$message.error('Please enter the expiration date')
        if (!this.form.first_name || !this.form.last_name) return this.$message.error('Please enter the name on the card')
        if (!this.form.consent) return this.$message.error('Please agree to the autopay terms')
        if (!this.form.signer_name.trim()) return this.$message.error('Please enter your name')
        const [month, year] = this.form.expiration.split('/')
        this.saving = true
        // the company's Stax customer first (made now if it has none), then the card is saved under it in Stax,
        // and only Stax's id for it is sent to us
        const customer = this.info.stax_customer_id
          ? Promise.resolve(this.info.stax_customer_id)
          : postActionApi('/payment/autopay-link/stax-customer', { token: this.token })
              .then(res => (this.info.stax_customer_id = res.data.stax_customer_id))
        customer.then(customerId => this.staxjs.tokenize({
          customer_id: customerId,
          firstname: this.form.first_name,
          lastname: this.form.last_name,
          month: month,
          year: year,
          method: 'card',
          validate: false,
        })).then(paymentMethod => postActionApi('/payment/autopay-link/card', {
          token: this.token,
          payment_method_id: paymentMethod.id,
          signer_name: this.form.signer_name.trim(),
          consent: true,
          consent_version: this.info.consent_version,
          include_past_due: this.form.include_past_due,
        })).then(res => {
          this.done = res.data
          // the secret is used up: drop it from the address bar
          history.replaceState(null, '', window.location.pathname)
        }).catch(err => {
          if (err && err.response) {
            this.$message.error(this.message(err, 'Unable to turn on autopay'))
            if (err.response.status == 409 || err.response.status == 410) this.load()
          } else {
            this.$message.error((err && err.message) || 'The card could not be saved. Please check it and try again.')
          }
        }).finally(() => {
          this.saving = false
        })
      },
      cardName(card) {
        return card ? (card.card_type ? card.card_type.toUpperCase() : 'card') + ' ending in ' + card.last4 : 'your card'
      },
      money(n) {
        return Number(n || 0).toFixed(2)
      },
      date(d) {
        return d ? moment(d).format('MMM D, YYYY') : ''
      },
      dateTime(d) {
        return d ? moment(d).format('MMM D, YYYY HH:mm') : ''
      },
    },
  }
</script>
<style scoped>
  *{box-sizing: border-box;}
  #setup{background-color:#F8F9FA;min-height: 100vh;padding: 40px 16px;display: flex;justify-content: center;align-items: flex-start;}
  .box{background-color: #ffffff;width: 100%;max-width: 680px;padding: 32px;border-radius: 16px;box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);display: grid;gap: 18px;}
  .logo{max-height: 48px;max-width: 180px;}
  .title{color:#2D3648;font-size: 22px;font-weight: bold;}
  .lead{font-size: 15px;color:#2D3648;margin: 0;line-height: 1.5;}
  .hint{font-size: 13px;color:#718096;margin: 0;}
  .center{text-align: center;}
  .group{display: grid;grid-template-columns: 1fr;grid-gap: 12px;}
  .groupDiv{display: grid;grid-template-columns: 1fr 1fr;grid-gap: 10px;}
  label{color: #8898AA;line-height: 34px;display: flex;flex-direction: column;font-size: 14px;}
  .input-box{height: 40px;line-height: 1;padding: 3px 10px 0 10px;border: 1px solid #DCDFE6;border-radius: 4px;width: 100%;font-size: 14px;}
  .names{display: flex;gap: 2%;}
  .names input{width: 49%;}
  .pastDue{background-color: #FFFAF0;border:1px solid #FBD38D;border-radius: 10px;padding: 12px 16px;margin-top: 16px;font-size: 14px;color:#2D3648;}
  .pastDue p{margin: 0 0 6px 0;}
  .pastDue ul{margin: 0 0 10px 0;padding-left: 20px;}
  .consent{display: flex;gap: 10px;align-items: flex-start;font-size: 14px;color:#4A5568;line-height: 1.5;margin-top: 16px;}
  .signer{margin-top: 10px;}
  .buttons{display: flex;justify-content: center;margin-top: 20px;}
  .btn{height:44px;min-width:86px;cursor: pointer;background-color: #5856D6;color:#ffffff;border-radius: 6px;font-size: 15px;display: inline-flex;align-items: center;justify-content: center;padding: 0 20px;}
  .btn.disabled{opacity: 0.5;}
  @media (max-width: 768px) {
    .box{padding: 20px 14px;}
  }
</style>
